const API_BASE = (import.meta.env.VITE_API_BASE ?? 'http://localhost:3000/v1').replace(/\/$/, '');

type TokenGetter = () => { access: string | null; refresh: string | null };
type TokenSetter = (tokens: { access: string | null; refresh: string | null }) => void;
type LogoutFn = () => void;

let getTokens: TokenGetter = () => ({ access: null, refresh: null });
let setTokens: TokenSetter = () => {};
let onUnauthorized: LogoutFn = () => {};

export function configureApiClient(opts: {
  getTokens: TokenGetter;
  setTokens: TokenSetter;
  onUnauthorized: LogoutFn;
}) {
  getTokens = opts.getTokens;
  setTokens = opts.setTokens;
  onUnauthorized = opts.onUnauthorized;
}

async function refresh(): Promise<string | null> {
  const { refresh } = getTokens();
  if (!refresh) return null;
  const r = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: refresh }),
  });
  if (!r.ok) return null;
  const data = (await r.json()) as { accessToken: string };
  setTokens({ access: data.accessToken, refresh });
  return data.accessToken;
}

export interface ApiError {
  status: number;
  message: string;
  validation?: unknown;
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const { access } = getTokens();
  const headers = new Headers(init.headers);
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body != null && !isFormData) headers.set('content-type', 'application/json');
  if (access) headers.set('authorization', `Bearer ${access}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401 && retry) {
    const newAccess = await refresh();
    if (newAccess) {
      return api<T>(path, init, false);
    }
    onUnauthorized();
    const err: ApiError = { status: 401, message: 'Sesión expirada' };
    throw err;
  }

  if (!res.ok) {
    let body: { message?: string; validation?: unknown } = {};
    try {
      body = await res.json();
    } catch {
      /* noop */
    }
    const err: ApiError = {
      status: res.status,
      message: body.message ?? `HTTP ${res.status}`,
      validation: body.validation,
    };
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const API = {
  get: <T = unknown>(path: string) => api<T>(path),
  post: <T = unknown>(path: string, body?: unknown) =>
    api<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T = unknown>(path: string, body?: unknown) =>
    api<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  del: <T = unknown>(path: string) => api<T>(path, { method: 'DELETE' }),
  upload: <T = unknown>(path: string, form: FormData) =>
    api<T>(path, { method: 'POST', body: form }),
};

export { API_BASE };
